import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Car, Plus, LogOut, Loader2, Eye, EyeOff, Copy, CheckCircle2, User, FileText, Award, 
  Upload, X, Image, File, Settings, Menu, DollarSign, Pencil, Trash2, Filter, Search,
  Shield, AlertCircle, CheckCircle, Wrench, Calendar, Edit
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_ppf-auto-manager/artifacts/h7byux7s_UP-Logo-White.png";

const PPF_TYPES = [
  { id: 'GLOSS', name: 'GLOSS', warranty: 10 },
  { id: 'MATT', name: 'MATT', warranty: 10 },
  { id: 'COLOR_PPF', name: 'COLOR PPF', warranty: 8 }
];

// Flattened coverage options - individual items
const COVERAGE_OPTIONS = [
  { id: 'loading_edge', name: 'Loading Edge', category: 'Small Parts' },
  { id: 'door_edge', name: 'Door Edge', category: 'Small Parts' },
  { id: 'front_bumper', name: 'Front Bumper', category: 'Front Package' },
  { id: 'hood', name: 'Hood', category: 'Front Package' },
  { id: 'side_mirrors', name: 'Side Mirrors', category: 'Front Package' },
  { id: 'fenders', name: 'Fenders', category: 'Front Package' },
  { id: 'premium_front_bumper', name: 'Front Bumper', category: 'Premium Package' },
  { id: 'premium_hood', name: 'Hood', category: 'Premium Package' },
  { id: 'premium_side_mirrors', name: 'Side Mirrors', category: 'Premium Package' },
  { id: 'premium_fenders', name: 'Fenders', category: 'Premium Package' },
  { id: 'side_skirts', name: 'Side Skirts', category: 'Premium Package' },
  { id: 'a_pillars', name: 'A-Pillars', category: 'Premium Package' },
  { id: 'roof_section', name: 'Roof Section', category: 'Premium Package' },
  { id: 'whole_car', name: 'Whole Car', category: 'Complete Package' },
  { id: 'headlights', name: 'Headlights', category: 'Extras' },
  { id: 'windshield', name: 'Windshield', category: 'Extras' },
  { id: 'interior_screens', name: 'Screens', category: 'Interior' },
  { id: 'interior_full', name: 'Full Interior', category: 'Interior' }
];

// Group coverage options by category
const COVERAGE_CATEGORIES = ['Small Parts', 'Front Package', 'Premium Package', 'Complete Package', 'Extras', 'Interior'];

// Sidebar Component
const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { path: '/partner', icon: Car, label: 'My Installations', exact: true },
    { path: '/partner/add-installation', icon: Plus, label: 'New Installation' },
    { path: '/partner/settings', icon: Settings, label: 'Account Settings' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const isActive = (path, exact) => exact ? location.pathname === path : location.pathname.startsWith(path);

  const navContent = (
    <>
      <div className="p-3 md:p-4 border-b border-white/5">
        <Link to="/" className="flex items-center justify-center">
          <img src={LOGO_URL} alt="Unique Protection" className="h-12 md:h-14 w-auto" />
        </Link>
      </div>

      <div className="p-3 md:p-4 border-b border-white/5">
        <div className="bg-zinc-900/50 rounded-xl p-3">
          <div className="flex items-center gap-3 mb-2">
            {user?.avatar_base64 ? (
              <img src={user.avatar_base64} alt="" className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-red-600/20 flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-white font-semibold text-sm truncate">{user?.name}</p>
              <p className="text-zinc-400 text-xs truncate">{user?.company_name || user?.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <span className={`inline-block px-3 py-1 ${user?.role === 'demo_partner' ? 'bg-red-600/20 text-red-400' : 'bg-emerald-600/20 text-emerald-400'} text-sm font-medium rounded-full`}>
              {user?.role === 'demo_partner' ? 'Demo Partner' : 'Partner'}
            </span>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-3 md:p-4 overflow-y-auto">
        <div className="space-y-1">
          {navItems.slice(0, 2).map((item) => (
            <Link key={item.path} to={item.path} onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-lg transition-colors duration-200 text-sm ${
                isActive(item.path, item.exact) ? 'bg-red-600/10 text-red-400' : 'text-zinc-400 hover:bg-white/5 hover:text-white'
              }`}>
              <item.icon className="w-4 h-4 md:w-5 md:h-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </div>
        
        {/* Separator before Account Settings */}
        <div className="my-4 border-t border-zinc-800" />
        
        {/* Account Settings */}
        <Link to={navItems[2].path} onClick={() => setMobileOpen(false)}
          className={`flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-lg transition-colors duration-200 text-sm ${
            isActive(navItems[2].path, navItems[2].exact) ? 'bg-red-600/10 text-red-400' : 'text-zinc-400 hover:bg-white/5 hover:text-white'
          }`}>
          {(() => { const Icon = navItems[2].icon; return <Icon className="w-4 h-4 md:w-5 md:h-5" />; })()}
          <span className="font-medium">{navItems[2].label}</span>
        </Link>
      </nav>

      <div className="p-3 md:p-4 border-t border-white/5">
        <Button data-testid="partner-logout-btn" onClick={handleLogout} variant="ghost"
          className="w-full justify-start gap-2 md:gap-3 text-zinc-400 hover:text-white hover:bg-white/5 h-9 md:h-10 text-sm">
          <LogOut className="w-4 h-4 md:w-5 md:h-5" /> Logout
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile/Tablet portrait sticky header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-[#09090b] border-b border-white/10 z-50 flex items-center justify-between px-4">
        <button onClick={() => setMobileOpen(!mobileOpen)} className="p-2 text-white">
          <Menu className="w-5 h-5" />
        </button>
        <img src={LOGO_URL} alt="Unique Protection" className="h-8 w-auto" />
      </div>
      
      {/* Mobile/Tablet sidebar overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/60 z-40" onClick={() => setMobileOpen(false)} />
      )}
      
      {/* Mobile/Tablet sidebar */}
      <div className={`lg:hidden fixed top-0 left-0 h-screen w-64 bg-[#09090b] border-r border-white/5 z-50 transform transition-transform ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {navContent}
      </div>
      
      {/* Desktop sidebar (1024px+) */}
      <div className="hidden lg:flex w-64 bg-[#09090b] border-r border-white/5 h-screen flex-col">
        {navContent}
      </div>
    </>
  );
};

// My Installations
const MyInstallations = () => {
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInstall, setSelectedInstall] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [editClientForm, setEditClientForm] = useState({ client_name: '', client_email: '', client_phone: '' });
  const [savingClient, setSavingClient] = useState(false);
  // Service Records state
  const [serviceRecords, setServiceRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [showAddServiceRecord, setShowAddServiceRecord] = useState(false);
  const [newServiceRecord, setNewServiceRecord] = useState({ service_date: new Date().toISOString().split('T')[0], description: '', warranty_related: false, notes: '' });
  const [savingRecord, setSavingRecord] = useState(false);
  // Warranty check filter and confirmation modal
  const [warrantyCheckFilter, setWarrantyCheckFilter] = useState('all');
  const [showWarrantyCheckConfirm, setShowWarrantyCheckConfirm] = useState(null); // installation to confirm
  const { getAuthHeaders } = useAuth();

  useEffect(() => { fetchInstallations(); }, []);

  const fetchInstallations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/installations`, { headers: getAuthHeaders() });
      setInstallations(response.data);
    } catch (error) {
      toast.error('Failed to load installations');
    } finally {
      setLoading(false);
    }
  };

  // Calculate warranty check counts
  const pendingCount = installations.filter(i => i.warranty_check_status === 'pending').length;
  const overdueCount = installations.filter(i => i.warranty_check_status === 'overdue').length;

  // Mark warranty check as completed (with confirmation)
  const handleCompleteWarrantyCheck = async (installId) => {
    try {
      await axios.put(`${API_URL}/api/installations/${installId}/warranty-check`, {}, { headers: getAuthHeaders() });
      toast.success('Warranty service marked as completed');
      setShowWarrantyCheckConfirm(null);
      fetchInstallations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update warranty check');
    }
  };

  const loadPhotos = async (installId) => {
    try {
      const response = await axios.get(`${API_URL}/api/installations/${installId}/photos`, { headers: getAuthHeaders() });
      setPhotos(response.data);
    } catch (err) {
      setPhotos([]);
    }
  };

  // Service Records functions
  const loadServiceRecords = async (installId) => {
    setLoadingRecords(true);
    try {
      const response = await axios.get(`${API_URL}/api/service-records/${installId}`, { headers: getAuthHeaders() });
      setServiceRecords(response.data);
    } catch (err) {
      setServiceRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  };

  const handleAddServiceRecord = async () => {
    if (!newServiceRecord.description.trim()) {
      toast.error('Description is required');
      return;
    }
    setSavingRecord(true);
    try {
      await axios.post(`${API_URL}/api/service-records`, {
        installation_id: selectedInstall.id,
        ...newServiceRecord
      }, { headers: getAuthHeaders() });
      toast.success('Service record added');
      loadServiceRecords(selectedInstall.id);
      setShowAddServiceRecord(false);
      setNewServiceRecord({ service_date: new Date().toISOString().split('T')[0], description: '', warranty_related: false, notes: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add service record');
    } finally {
      setSavingRecord(false);
    }
  };

  const handleDeleteServiceRecord = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this service record?')) return;
    try {
      await axios.delete(`${API_URL}/api/service-records/${recordId}`, { headers: getAuthHeaders() });
      toast.success('Service record deleted');
      loadServiceRecords(selectedInstall.id);
    } catch (error) {
      toast.error('Failed to delete service record');
    }
  };

  const copyAccessCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Access code copied!');
  };

  // P1.4: Partner can toggle warranty service
  const handleToggleWarrantyService = async (inst) => {
    try {
      const newStatus = !inst.warranty_service;
      await axios.patch(
        `${API_URL}/api/installations/${inst.id}/partner`,
        {
          warranty_service: newStatus,
          warranty_service_date: newStatus ? new Date().toISOString().split('T')[0] : null
        },
        { headers: getAuthHeaders() }
      );
      toast.success(newStatus ? 'Warranty service marked complete' : 'Warranty service marked pending');
      fetchInstallations();
      // Update selected install if it's the same one
      if (selectedInstall?.id === inst.id) {
        setSelectedInstall(prev => ({
          ...prev,
          warranty_service: newStatus,
          warranty_service_date: newStatus ? new Date().toISOString().split('T')[0] : null
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update warranty service');
    }
  };

  // P1.4: Start editing client info
  const startEditingClient = () => {
    setEditClientForm({
      client_name: selectedInstall.client_name || '',
      client_email: selectedInstall.client_email || '',
      client_phone: selectedInstall.client_phone || ''
    });
    setIsEditingClient(true);
  };

  // P1.4: Save client info edits
  const saveClientEdits = async () => {
    if (!editClientForm.client_name || !editClientForm.client_email) {
      toast.error('Client name and email are required');
      return;
    }
    
    setSavingClient(true);
    try {
      const response = await axios.patch(
        `${API_URL}/api/installations/${selectedInstall.id}/partner`,
        {
          client_name: editClientForm.client_name,
          client_email: editClientForm.client_email,
          client_phone: editClientForm.client_phone || null
        },
        { headers: getAuthHeaders() }
      );
      toast.success('Client information updated');
      setIsEditingClient(false);
      fetchInstallations();
      // Update selected install with new data
      setSelectedInstall(prev => ({
        ...prev,
        client_name: editClientForm.client_name,
        client_email: editClientForm.client_email,
        client_phone: editClientForm.client_phone
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client info');
    } finally {
      setSavingClient(false);
    }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const getCoverageList = (coverage) => {
    if (!coverage) return [];
    // Handle both old and new coverage formats
    const allNames = {
      // New flattened items
      loading_edge: 'Loading Edge', door_edge: 'Door Edge',
      front_bumper: 'Front Bumper', hood: 'Hood', side_mirrors: 'Side Mirrors', fenders: 'Fenders',
      premium_front_bumper: 'Front Bumper (Premium)', premium_hood: 'Hood (Premium)',
      premium_side_mirrors: 'Side Mirrors (Premium)', premium_fenders: 'Fenders (Premium)',
      side_skirts: 'Side Skirts', a_pillars: 'A-Pillars', roof_section: 'Roof Section',
      whole_car: 'Whole Car', headlights: 'Headlights', windshield: 'Windshield',
      // Interior items (NEW)
      interior_screens: 'Screens', interior_full: 'Full Interior',
      // Legacy items
      small_parts: 'Small Parts', front_package: 'Front Package', premium_package: 'Premium Package',
      complete_package: 'Complete Package', extras_headlights: 'Headlights', extras_windshield: 'Windshield'
    };
    return Object.entries(coverage).filter(([_, v]) => v).map(([k]) => allNames[k] || k.replace(/_/g, ' '));
  };

  const openDetail = async (inst) => {
    setSelectedInstall(inst);
    if (inst.has_photos) await loadPhotos(inst.id);
    loadServiceRecords(inst.id);
  };

  // Filter installations by search term (vehicle info)
  const filteredInstallations = installations.filter(inst => {
    // Apply warranty check filter first
    if (warrantyCheckFilter === 'pending' && inst.warranty_check_status !== 'pending') return false;
    if (warrantyCheckFilter === 'overdue' && inst.warranty_check_status !== 'overdue') return false;
    if (warrantyCheckFilter === 'completed' && inst.warranty_check_status !== 'completed') return false;
    
    // Then apply search term
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      inst.vin?.toLowerCase().includes(term) ||
      inst.license_plate?.toLowerCase().includes(term) ||
      inst.client_name?.toLowerCase().includes(term) ||
      inst.brand?.toLowerCase().includes(term) ||
      inst.model?.toLowerCase().includes(term) ||
      `${inst.brand} ${inst.model}`.toLowerCase().includes(term) ||
      inst.access_code?.toLowerCase().includes(term)
    );
  });

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>;

  const totalRevenue = filteredInstallations.reduce((sum, i) => sum + (i.service_price || 0), 0);

  return (
    <div data-testid="partner-installations" className="w-full">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
        <h1 className="font-['Chivo'] text-xl md:text-2xl font-bold text-white">My Installations</h1>
        <Link to="/partner/add-installation">
          <Button className="bg-red-600 hover:bg-red-700 text-white gap-2 h-10 text-sm w-full sm:w-auto"><Plus className="w-4 h-4" /> New Installation</Button>
        </Link>
      </div>

      {/* Actions Needed Section */}
      {(pendingCount > 0 || overdueCount > 0) && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 mb-6">
          <h2 className="font-['Chivo'] text-lg text-white mb-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-500" /> Actions Needed
          </h2>
          <div className="flex flex-wrap gap-3">
            {pendingCount > 0 && (
              <button
                onClick={() => setWarrantyCheckFilter(warrantyCheckFilter === 'pending' ? 'all' : 'pending')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  warrantyCheckFilter === 'pending' 
                    ? 'bg-yellow-600/30 border-2 border-yellow-500 text-yellow-400' 
                    : 'bg-yellow-600/10 border border-yellow-500/20 text-yellow-400 hover:bg-yellow-600/20'
                }`}
              >
                <div className="w-2 h-2 rounded-full bg-yellow-500" />
                <span className="text-sm font-medium">Warranty checks needed: {pendingCount}</span>
              </button>
            )}
            {overdueCount > 0 && (
              <button
                onClick={() => setWarrantyCheckFilter(warrantyCheckFilter === 'overdue' ? 'all' : 'overdue')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  warrantyCheckFilter === 'overdue' 
                    ? 'bg-red-600/30 border-2 border-red-500 text-red-400' 
                    : 'bg-red-600/10 border border-red-500/20 text-red-400 hover:bg-red-600/20'
                }`}
              >
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-sm font-medium">Overdue warranty checks: {overdueCount}</span>
              </button>
            )}
            {warrantyCheckFilter !== 'all' && (
              <button
                onClick={() => setWarrantyCheckFilter('all')}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-800 text-zinc-400 hover:bg-zinc-700 transition-colors"
              >
                <X className="w-4 h-4" />
                <span className="text-sm">Clear filter</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Search Box */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by vehicle, VIN, plate, client..."
            className="pl-10 bg-zinc-900 border-zinc-800 text-white h-10"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-6 mb-4 md:mb-8">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2 md:p-6">
          <p className="text-zinc-400 text-[10px] md:text-sm mb-0.5 md:mb-1">Total</p>
          <p className="font-['Chivo'] text-lg md:text-3xl font-bold text-white">{installations.length}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2 md:p-6">
          <p className="text-zinc-400 text-[10px] md:text-sm mb-0.5 md:mb-1">Active</p>
          <p className="font-['Chivo'] text-lg md:text-3xl font-bold text-emerald-400">{installations.filter(i => i.warranty_active).length}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2 md:p-6">
          <p className="text-zinc-400 text-[10px] md:text-sm mb-0.5 md:mb-1">This Month</p>
          <p className="font-['Chivo'] text-lg md:text-3xl font-bold text-red-400">
            {installations.filter(i => {
              const d = new Date(i.install_date);
              const now = new Date();
              return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
            }).length}
          </p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2 md:p-6">
          <p className="text-zinc-400 text-[10px] md:text-sm mb-0.5 md:mb-1">Revenue</p>
          <p className="font-['Chivo'] text-lg md:text-3xl font-bold text-amber-400">€{totalRevenue.toLocaleString()}</p>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left p-1.5 md:p-3 text-zinc-400 font-medium text-[10px] md:text-xs uppercase">Vehicle</th>
                <th className="text-left p-1.5 md:p-3 text-zinc-400 font-medium text-[10px] md:text-xs uppercase hidden lg:table-cell">Client</th>
                <th className="text-left p-1.5 md:p-3 text-zinc-400 font-medium text-[10px] md:text-xs uppercase">PPF</th>
                <th className="text-left p-1.5 md:p-3 text-zinc-400 font-medium text-[10px] md:text-xs uppercase hidden lg:table-cell">Price</th>
                <th className="text-left p-1.5 md:p-3 text-zinc-400 font-medium text-[10px] md:text-xs uppercase">Warranty</th>
                <th className="text-left p-1.5 md:p-3 text-zinc-400 font-medium text-[10px] md:text-xs uppercase">Warranty Check</th>
                <th className="text-left p-1.5 md:p-3 text-zinc-400 font-medium text-[10px] md:text-xs uppercase hidden lg:table-cell">Code</th>
                <th className="text-right p-1.5 md:p-3 text-zinc-400 font-medium text-[10px] md:text-xs uppercase w-20 md:w-24"></th>
              </tr>
            </thead>
            <tbody>
              {filteredInstallations.map((inst) => (
                <tr key={inst.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                  <td className="p-1.5 md:p-3">
                    <p className="text-white font-medium text-xs md:text-sm">{inst.year} {inst.brand}</p>
                    <p className="text-zinc-400 text-[10px] md:text-xs">{inst.model}</p>
                    <p className="text-zinc-400 text-[10px] lg:hidden truncate max-w-[100px]">{inst.client_name}</p>
                  </td>
                  <td className="p-1.5 md:p-3 hidden lg:table-cell"><p className="text-zinc-300 text-sm">{inst.client_name}</p></td>
                  <td className="p-1.5 md:p-3">
                    <span className="text-white text-xs md:text-sm">{inst.ppf_type?.replace('_', ' ')}</span>
                    <p className="text-amber-400 text-[10px] lg:hidden">{inst.service_price ? `€${inst.service_price.toLocaleString()}` : ''}</p>
                  </td>
                  <td className="p-1.5 md:p-3 hidden lg:table-cell">
                    {inst.service_price ? (
                      <span className="text-amber-400 font-medium text-sm">€{inst.service_price.toLocaleString()}</span>
                    ) : <span className="text-zinc-600">-</span>}
                  </td>
                  <td className="p-1.5 md:p-3">
                    <span className={`inline-flex items-center px-1 md:px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium whitespace-nowrap ${inst.warranty_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                      {inst.warranty_active ? 'Active' : 'Expired'}
                    </span>
                  </td>
                  <td className="p-1.5 md:p-3">
                    {inst.warranty_check_status === 'completed' ? (
                      <span className="inline-flex items-center gap-1 px-1.5 md:px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-emerald-500/10 text-emerald-400">
                        <CheckCircle className="w-3 h-3" />
                        <span className="hidden sm:inline">Completed</span>
                      </span>
                    ) : inst.warranty_check_status === 'overdue' ? (
                      <button 
                        onClick={(e) => { e.stopPropagation(); setShowWarrantyCheckConfirm(inst); }}
                        className="inline-flex items-center gap-1 px-1.5 md:px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                      >
                        <AlertCircle className="w-3 h-3" />
                        <span className="hidden sm:inline">Overdue</span>
                      </button>
                    ) : (
                      <button 
                        onClick={(e) => { e.stopPropagation(); setShowWarrantyCheckConfirm(inst); }}
                        className="inline-flex items-center gap-1 px-1.5 md:px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 transition-colors"
                      >
                        <Calendar className="w-3 h-3" />
                        <span className="hidden sm:inline">Pending</span>
                      </button>
                    )}
                  </td>
                  <td className="p-1.5 md:p-3 hidden lg:table-cell">
                    <button onClick={() => copyAccessCode(inst.access_code)}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-800 hover:bg-zinc-700 rounded text-xs font-mono text-red-400">
                      {inst.access_code} <Copy className="w-3 h-3 flex-shrink-0" />
                    </button>
                  </td>
                  <td className="p-1.5 md:p-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => openDetail(inst)} className="text-zinc-400 hover:text-white h-7 md:h-8 px-2 text-xs">
                      <Edit className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1" />
                      <span className="hidden sm:inline">View / Edit</span>
                    </Button>
                  </td>
                </tr>
              ))}
              {filteredInstallations.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-zinc-500">{searchTerm || warrantyCheckFilter !== 'all' ? 'No installations match your filters.' : 'No installations yet. Add your first one!'}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Warranty Check Confirmation Modal */}
      <Dialog open={!!showWarrantyCheckConfirm} onOpenChange={() => setShowWarrantyCheckConfirm(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-xl flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" /> Confirm Warranty Service
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-300 mb-4">
              Confirm that the warranty check/service has been completed for this installation:
            </p>
            {showWarrantyCheckConfirm && (
              <div className="bg-zinc-950/50 rounded-lg p-3 mb-4">
                <p className="text-white font-medium">{showWarrantyCheckConfirm.year} {showWarrantyCheckConfirm.brand} {showWarrantyCheckConfirm.model}</p>
                <p className="text-zinc-400 text-sm">{showWarrantyCheckConfirm.client_name}</p>
                <p className="text-zinc-500 text-xs font-mono">{showWarrantyCheckConfirm.vin}</p>
              </div>
            )}
            <p className="text-zinc-500 text-sm">
              This action marks the warranty service as completed and cannot be undone.
            </p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="ghost" onClick={() => setShowWarrantyCheckConfirm(null)} className="flex-1">
              Cancel
            </Button>
            <Button 
              onClick={() => handleCompleteWarrantyCheck(showWarrantyCheckConfirm?.id)} 
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            >
              <CheckCircle className="w-4 h-4 mr-2" /> Confirm Completed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={!!selectedInstall} onOpenChange={() => { setSelectedInstall(null); setPhotos([]); setIsEditingClient(false); setServiceRecords([]); setShowAddServiceRecord(false); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Chivo'] text-xl">Installation Details</DialogTitle></DialogHeader>
          {selectedInstall && (
            <div className="space-y-6">
              <div className="bg-red-600/10 border border-red-500/20 rounded-lg p-4">
                <p className="text-sm text-zinc-400 mb-1">Client Access Code</p>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xl text-red-400">{selectedInstall.access_code}</span>
                  <Button size="sm" variant="ghost" onClick={() => copyAccessCode(selectedInstall.access_code)} className="text-red-400">
                    <Copy className="w-4 h-4 mr-2" /> Copy
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-zinc-950/50 rounded-lg p-4">
                  <h3 className="text-sm text-zinc-400 mb-2 flex items-center gap-2"><Car className="w-4 h-4" /> Vehicle</h3>
                  <p className="text-white">{selectedInstall.year} {selectedInstall.brand} {selectedInstall.model}</p>
                  <p className="text-zinc-500 text-sm font-mono">{selectedInstall.vin}</p>
                  <p className="text-zinc-400 text-sm">{selectedInstall.original_color} • {selectedInstall.license_plate}</p>
                </div>
                
                {/* Client Info - EDITABLE Section with Clear Visual Indicator */}
                <div className={`rounded-lg p-4 transition-all ${
                  isEditingClient 
                    ? 'bg-blue-950/30 border-2 border-blue-500/50' 
                    : 'bg-gradient-to-br from-zinc-900/80 to-zinc-950/80 border-2 border-dashed border-zinc-700 hover:border-blue-500/50'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm text-zinc-400 flex items-center gap-2">
                      <User className="w-4 h-4" /> Client
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-600/20 text-blue-400 rounded">EDITABLE</span>
                    </h3>
                    {!isEditingClient && (
                      <Button 
                        size="sm" 
                        onClick={startEditingClient} 
                        className="bg-blue-600 hover:bg-blue-700 text-white h-7 px-3 text-xs font-medium"
                      >
                        <Pencil className="w-3 h-3 mr-1.5" /> Edit
                      </Button>
                    )}
                  </div>
                  
                  {isEditingClient ? (
                    <div className="space-y-2.5">
                      <div>
                        <Label className="text-zinc-400 text-xs">Name *</Label>
                        <Input 
                          value={editClientForm.client_name} 
                          onChange={(e) => setEditClientForm({...editClientForm, client_name: e.target.value})}
                          className="mt-1 bg-zinc-950 border-zinc-600 text-white h-8 text-sm focus:border-blue-500"
                          autoFocus
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Email *</Label>
                        <Input 
                          type="email"
                          value={editClientForm.client_email} 
                          onChange={(e) => setEditClientForm({...editClientForm, client_email: e.target.value})}
                          className="mt-1 bg-zinc-950 border-zinc-600 text-white h-8 text-sm focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Phone</Label>
                        <Input 
                          value={editClientForm.client_phone} 
                          onChange={(e) => setEditClientForm({...editClientForm, client_phone: e.target.value})}
                          className="mt-1 bg-zinc-950 border-zinc-600 text-white h-8 text-sm focus:border-blue-500"
                          placeholder="+1234567890"
                        />
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button size="sm" onClick={saveClientEdits} disabled={savingClient} className="flex-1 bg-blue-600 hover:bg-blue-700 h-8 text-xs font-medium">
                          {savingClient ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <CheckCircle className="w-3 h-3 mr-1.5" />}
                          Save Changes
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setIsEditingClient(false)} className="h-8 text-xs border-zinc-600 text-zinc-300 hover:bg-zinc-800">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <p className="text-white font-medium">{selectedInstall.client_name}</p>
                      <p className="text-zinc-400 text-sm">{selectedInstall.client_email}</p>
                      {selectedInstall.client_phone && <p className="text-zinc-500 text-sm">{selectedInstall.client_phone}</p>}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-zinc-950/50 rounded-lg p-4">
                <h3 className="text-sm text-zinc-400 mb-3 flex items-center gap-2"><Award className="w-4 h-4" /> Installation & Warranty</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><p className="text-zinc-500">Date</p><p className="text-white">{formatDate(selectedInstall.install_date)}</p></div>
                  <div><p className="text-zinc-500">PPF Type</p><p className="text-white">{selectedInstall.ppf_type?.replace('_', ' ')}{selectedInstall.ppf_color && ` - ${selectedInstall.ppf_color}`}</p></div>
                  <div><p className="text-zinc-500">Warranty</p><p className="text-white">{selectedInstall.warranty_years} Years</p></div>
                  <div><p className="text-zinc-500">Expires</p><p className={selectedInstall.warranty_active ? 'text-emerald-400' : 'text-red-400'}>{formatDate(selectedInstall.warranty_expiration)}</p></div>
                  {selectedInstall.service_price && <div><p className="text-zinc-500">Service Price</p><p className="text-amber-400 font-medium">€{selectedInstall.service_price.toLocaleString()}</p></div>}
                </div>
              </div>

              {/* Warranty Check Status Section */}
              <div className={`rounded-lg p-4 transition-all ${
                selectedInstall.warranty_check_status === 'completed' 
                  ? 'bg-emerald-950/20 border border-emerald-500/30' 
                  : selectedInstall.warranty_check_status === 'overdue'
                    ? 'bg-red-950/20 border border-red-500/30'
                    : 'bg-yellow-950/20 border border-yellow-500/30'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm text-zinc-300 flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4" /> Warranty Check (30-day inspection)
                    </h3>
                    <p className="text-xs text-zinc-500">
                      {selectedInstall.warranty_check_status === 'completed' 
                        ? `Completed on ${selectedInstall.warranty_check_completed_at ? formatDate(selectedInstall.warranty_check_completed_at) : 'N/A'}`
                        : selectedInstall.warranty_check_status === 'overdue'
                          ? 'Overdue - contact client to schedule inspection'
                          : 'Pending - client needs 30-day warranty inspection'
                      }
                    </p>
                  </div>
                  {selectedInstall.warranty_check_status !== 'completed' && (
                    <Button 
                      onClick={() => setShowWarrantyCheckConfirm(selectedInstall)}
                      className={`${
                        selectedInstall.warranty_check_status === 'overdue' 
                          ? 'bg-red-600 hover:bg-red-700' 
                          : 'bg-yellow-600 hover:bg-yellow-700'
                      } text-white text-sm`}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" /> Mark Completed
                    </Button>
                  )}
                  {selectedInstall.warranty_check_status === 'completed' && (
                    <span className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600/20 text-emerald-400 rounded-full text-sm font-medium">
                      <CheckCircle className="w-4 h-4" /> Completed
                    </span>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-sm text-zinc-400 mb-2">Coverage Areas</h3>
                <div className="flex flex-wrap gap-2">
                  {getCoverageList(selectedInstall.coverage_areas).map((item) => (
                    <span key={item} className="px-3 py-1 bg-zinc-800 rounded-full text-sm text-zinc-300">{item}</span>
                  ))}
                </div>
              </div>

              {selectedInstall.notes && (
                <div><h3 className="text-sm text-zinc-400 mb-2">Notes</h3><p className="text-zinc-300 text-sm bg-zinc-950/50 rounded-lg p-3">{selectedInstall.notes}</p></div>
              )}

              {/* Warranty Service Section - EDITABLE with Clear Visual Indicator */}
              <div className={`rounded-lg p-4 transition-all ${
                selectedInstall.warranty_service 
                  ? 'bg-emerald-950/20 border-2 border-emerald-500/30' 
                  : 'bg-gradient-to-br from-zinc-900/80 to-amber-950/20 border-2 border-dashed border-amber-500/30'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="text-sm text-zinc-300 flex items-center gap-2">
                      <Shield className="w-4 h-4" /> Warranty Service
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-600/20 text-blue-400 rounded">EDITABLE</span>
                    </h3>
                  </div>
                  
                  {/* Toggle Switch Style */}
                  <button
                    onClick={() => handleToggleWarrantyService(selectedInstall)}
                    className={`relative inline-flex h-8 w-44 items-center rounded-full transition-colors ${
                      selectedInstall.warranty_service 
                        ? 'bg-emerald-600' 
                        : 'bg-zinc-700 hover:bg-zinc-600'
                    }`}
                  >
                    <span className={`absolute left-2 text-xs font-medium transition-opacity ${
                      selectedInstall.warranty_service ? 'opacity-100 text-white' : 'opacity-0'
                    }`}>
                      ✓ Completed
                    </span>
                    <span className={`absolute right-2 text-xs font-medium transition-opacity ${
                      !selectedInstall.warranty_service ? 'opacity-100 text-zinc-300' : 'opacity-0'
                    }`}>
                      Pending
                    </span>
                    <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                      selectedInstall.warranty_service ? 'translate-x-[9.5rem]' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                
                {selectedInstall.warranty_service && selectedInstall.warranty_service_date && (
                  <p className="text-emerald-400/70 text-xs mt-2 pl-6">
                    Inspection completed on {new Date(selectedInstall.warranty_service_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                )}
                {!selectedInstall.warranty_service && (
                  <p className="text-amber-400/70 text-xs mt-2 pl-6">
                    Post-installation inspection required for warranty validation
                  </p>
                )}
              </div>

              {/* Service Records Section */}
              <div className="border-t border-zinc-800 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm text-zinc-400 flex items-center gap-2">
                    <Wrench className="w-4 h-4" /> Service History
                  </h3>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowAddServiceRecord(!showAddServiceRecord)}
                    className="h-7 px-2 text-xs border-zinc-700 text-zinc-400 hover:text-white"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add Record
                  </Button>
                </div>

                {/* Add Service Record Form */}
                {showAddServiceRecord && (
                  <div className="bg-zinc-950/50 rounded-lg p-3 mb-3 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-zinc-400 text-xs">Service Date</Label>
                        <Input 
                          type="date" 
                          value={newServiceRecord.service_date}
                          onChange={(e) => setNewServiceRecord({...newServiceRecord, service_date: e.target.value})}
                          className="mt-1 h-8 text-xs bg-zinc-900 border-zinc-700 text-white"
                        />
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox 
                            checked={newServiceRecord.warranty_related}
                            onCheckedChange={(checked) => setNewServiceRecord({...newServiceRecord, warranty_related: checked})}
                          />
                          <span className="text-xs text-zinc-400">Warranty Related</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">Description *</Label>
                      <Input 
                        value={newServiceRecord.description}
                        onChange={(e) => setNewServiceRecord({...newServiceRecord, description: e.target.value})}
                        placeholder="What was done..."
                        className="mt-1 h-8 text-xs bg-zinc-900 border-zinc-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">Notes (optional)</Label>
                      <Textarea 
                        value={newServiceRecord.notes}
                        onChange={(e) => setNewServiceRecord({...newServiceRecord, notes: e.target.value})}
                        placeholder="Additional notes..."
                        className="mt-1 text-xs bg-zinc-900 border-zinc-700 text-white min-h-[60px]"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={handleAddServiceRecord}
                        disabled={savingRecord}
                        className="h-7 px-3 text-xs bg-red-600 hover:bg-red-700"
                      >
                        {savingRecord ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Save
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => setShowAddServiceRecord(false)}
                        className="h-7 px-3 text-xs text-zinc-400"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {/* Service Records List */}
                {loadingRecords ? (
                  <p className="text-zinc-500 text-xs">Loading records...</p>
                ) : serviceRecords.length === 0 ? (
                  <p className="text-zinc-500 text-xs">No service records yet</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {serviceRecords.map((record) => (
                      <div key={record.id} className="bg-zinc-950/50 rounded-lg p-3 group">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-zinc-500 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(record.service_date).toLocaleDateString()}
                              </span>
                              {record.warranty_related && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-emerald-600/20 text-emerald-400 rounded">WARRANTY</span>
                              )}
                            </div>
                            <p className="text-white text-sm">{record.description}</p>
                            {record.notes && <p className="text-zinc-500 text-xs mt-1">{record.notes}</p>}
                            <p className="text-zinc-600 text-[10px] mt-1">Added by {record.created_by_name || 'Unknown'}</p>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteServiceRecord(record.id)}
                            className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0 text-zinc-500 hover:text-red-400"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {photos.length > 0 && (
                <div>
                  <h3 className="text-sm text-zinc-400 mb-2 flex items-center gap-2"><Image className="w-4 h-4" /> Photos</h3>
                  <div className="grid grid-cols-4 gap-2">
                    {photos.map((p, i) => (
                      <img key={i} src={p.data} alt="" className="aspect-square rounded-lg object-cover" />
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-4 text-sm">
                {selectedInstall.has_warranty_card && <span className="flex items-center gap-1 text-emerald-400"><File className="w-4 h-4" /> Warranty Card</span>}
                {selectedInstall.has_photos && <span className="flex items-center gap-1 text-red-400"><Image className="w-4 h-4" /> {selectedInstall.photo_count} Photos</span>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Add Installation
const AddInstallation = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const warrantyCardRef = useRef(null);
  const photosRef = useRef(null);
  
  const [vehicle, setVehicle] = useState({ vin: '', brand: '', model: '', year: new Date().getFullYear(), original_color: '', license_plate: '' });
  const [client, setClient] = useState({ full_name: '', email: '', phone: '' });
  const [installation, setInstallation] = useState({ install_date: new Date().toISOString().split('T')[0], ppf_type: '', ppf_color: '', roll_number: '', service_price: '', notes: '' });
  // New flattened coverage state
  const [coverage, setCoverage] = useState({
    loading_edge: false, door_edge: false,
    front_bumper: false, hood: false, side_mirrors: false, fenders: false,
    premium_front_bumper: false, premium_hood: false, premium_side_mirrors: false, premium_fenders: false,
    side_skirts: false, a_pillars: false, roof_section: false,
    whole_car: false, headlights: false, windshield: false
  });
  const [warrantyCard, setWarrantyCard] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [createdInstall, setCreatedInstall] = useState(null);

  const validateVIN = (v) => v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 17);
  const selectedPPF = PPF_TYPES.find(p => p.id === installation.ppf_type);
  const warrantyYears = selectedPPF?.warranty || 0;

  const handleWarrantyCardChange = async (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 5 * 1024 * 1024) {
      const base64 = await fileToBase64(file);
      setWarrantyCard({ name: file.name, data: base64 });
    } else if (file) toast.error('File too large. Max 5MB');
  };

  const handlePhotosChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length + photos.length > 4) { toast.error('Max 4 photos'); return; }
    const newPhotos = [];
    for (const file of files) {
      if (file.size <= 5 * 1024 * 1024) {
        newPhotos.push({ name: file.name, data: await fileToBase64(file) });
      }
    }
    setPhotos([...photos, ...newPhotos]);
  };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (vehicle.vin.length !== 17) { toast.error('VIN must be 17 characters'); return; }
    if (!vehicle.brand || !vehicle.model || !vehicle.original_color || !vehicle.license_plate) { toast.error('Fill all vehicle info'); return; }
    if (!client.full_name || !client.email) { toast.error('Enter client name and email'); return; }
    if (!installation.ppf_type) { toast.error('Select PPF type'); return; }
    if (installation.ppf_type === 'COLOR_PPF' && !installation.ppf_color) { toast.error('Enter PPF color'); return; }
    if (!installation.roll_number) { toast.error('Enter roll number'); return; }
    if (!Object.values(coverage).some(v => v)) { toast.error('Select at least one coverage area'); return; }

    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/installations`, {
        vehicle: { ...vehicle, year: parseInt(vehicle.year) },
        client: { ...client, phone: client.phone || null },
        install_date: new Date(installation.install_date).toISOString(),
        ppf_type: installation.ppf_type,
        ppf_color: installation.ppf_type === 'COLOR_PPF' ? installation.ppf_color : null,
        roll_number: installation.roll_number,
        coverage_areas: coverage,
        service_price: installation.service_price ? parseFloat(installation.service_price) : null,
        notes: installation.notes || null,
        warranty_card_base64: warrantyCard?.data || null,
        warranty_card_filename: warrantyCard?.name || null,
        photos_base64: photos.map(p => p.data)
      }, { headers: getAuthHeaders() });

      setCreatedInstall(response.data);
      toast.success('Installation recorded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create installation');
    } finally {
      setSaving(false);
    }
  };

  const copyAccessCode = (code) => { navigator.clipboard.writeText(code); toast.success('Copied!'); };

  const resetForm = () => {
    setCreatedInstall(null);
    setVehicle({ vin: '', brand: '', model: '', year: new Date().getFullYear(), original_color: '', license_plate: '' });
    setClient({ full_name: '', email: '', phone: '' });
    setInstallation({ install_date: new Date().toISOString().split('T')[0], ppf_type: '', ppf_color: '', roll_number: '', service_price: '', notes: '' });
    setCoverage({
      loading_edge: false, door_edge: false,
      front_bumper: false, hood: false, side_mirrors: false, fenders: false,
      premium_front_bumper: false, premium_hood: false, premium_side_mirrors: false, premium_fenders: false,
      side_skirts: false, a_pillars: false, roof_section: false,
      whole_car: false, headlights: false, windshield: false
    });
    setWarrantyCard(null);
    setPhotos([]);
  };

  if (createdInstall) {
    return (
      <div data-testid="installation-success" className="w-full max-w-4xl mx-auto px-4">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 md:p-8 text-center">
          <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-emerald-500" />
          </div>
          <h1 className="font-['Chivo'] text-xl md:text-2xl font-bold text-white mb-2">Installation Recorded!</h1>
          <p className="text-zinc-400 mb-6 md:mb-8 text-sm md:text-base">Share the access code with your client.</p>
          <div className="bg-red-600/10 border border-red-500/20 rounded-lg p-4 md:p-6 mb-6 md:mb-8">
            <p className="text-sm text-zinc-400 mb-2">Client Access Code</p>
            <div className="flex items-center justify-center gap-4">
              <span className="font-mono text-2xl md:text-3xl text-red-400">{createdInstall.access_code}</span>
              <Button size="sm" variant="ghost" onClick={() => copyAccessCode(createdInstall.access_code)} className="text-red-400"><Copy className="w-5 h-5" /></Button>
            </div>
          </div>
          <div className="bg-zinc-950/50 rounded-lg p-4 text-left mb-6 md:mb-8 text-sm grid grid-cols-2 gap-4">
            <div><p className="text-zinc-500">Vehicle</p><p className="text-white">{createdInstall.year} {createdInstall.brand} {createdInstall.model}</p></div>
            <div><p className="text-zinc-500">Client</p><p className="text-white">{createdInstall.client_name}</p></div>
            <div><p className="text-zinc-500">Warranty</p><p className="text-white">{createdInstall.warranty_years} Years</p></div>
            {createdInstall.service_price && <div><p className="text-zinc-500">Price</p><p className="text-amber-400">€{createdInstall.service_price}</p></div>}
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button variant="outline" onClick={resetForm} className="flex-1 border-zinc-700 text-zinc-300">Add Another</Button>
            <Button onClick={() => navigate('/partner')} className="flex-1 bg-red-600 hover:bg-red-700">View All</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="partner-add-installation" className="w-full">
      <h1 className="font-['Chivo'] text-xl md:text-2xl font-bold text-white mb-6 md:mb-8">New Installation</h1>
      <form onSubmit={handleSubmit} className="w-full max-w-6xl mx-auto space-y-6">
        {/* Vehicle Section */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="px-4 md:px-6 py-3 md:py-4 border-b border-zinc-800 flex items-center gap-3">
            <Car className="w-5 h-5 text-red-500" />
            <h2 className="font-['Chivo'] text-base md:text-lg font-bold text-white">Vehicle Information</h2>
          </div>
          <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="md:col-span-2">
              <Label className="text-zinc-300 text-sm">VIN *</Label>
              <div className="relative mt-1.5">
                <Input data-testid="vehicle-vin-input" value={vehicle.vin}
                  onChange={(e) => setVehicle({ ...vehicle, vin: validateVIN(e.target.value) })}
                  placeholder="17-character VIN" maxLength={17}
                  className="font-mono tracking-wider bg-zinc-950 border-zinc-700 text-white uppercase h-10 md:h-12 text-sm" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500">{vehicle.vin.length}/17</span>
              </div>
            </div>
            <div><Label className="text-zinc-300 text-sm">Brand *</Label><Input value={vehicle.brand} onChange={(e) => setVehicle({ ...vehicle, brand: e.target.value })} placeholder="BMW, Mercedes" className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" /></div>
            <div><Label className="text-zinc-300 text-sm">Model *</Label><Input value={vehicle.model} onChange={(e) => setVehicle({ ...vehicle, model: e.target.value })} placeholder="M3, S-Class" className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" /></div>
            <div><Label className="text-zinc-300 text-sm">Year *</Label><Input type="number" value={vehicle.year} onChange={(e) => setVehicle({ ...vehicle, year: e.target.value })} min="1990" max={new Date().getFullYear() + 1} className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" /></div>
            <div><Label className="text-zinc-300 text-sm">Original Color *</Label><Input value={vehicle.original_color} onChange={(e) => setVehicle({ ...vehicle, original_color: e.target.value })} placeholder="Black, White" className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" /></div>
            <div className="md:col-span-2"><Label className="text-zinc-300 text-sm">License Plate *</Label><Input value={vehicle.license_plate} onChange={(e) => setVehicle({ ...vehicle, license_plate: e.target.value.toUpperCase() })} placeholder="ABC 1234" className="mt-1.5 bg-zinc-950 border-zinc-700 text-white uppercase h-10 text-sm" /></div>
          </div>
        </div>

        {/* Client Section */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="px-4 md:px-6 py-3 md:py-4 border-b border-zinc-800 flex items-center gap-3">
            <User className="w-5 h-5 text-red-500" />
            <h2 className="font-['Chivo'] text-base md:text-lg font-bold text-white">Client Information</h2>
          </div>
          <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="md:col-span-2"><Label className="text-zinc-300 text-sm">Full Name *</Label><Input value={client.full_name} onChange={(e) => setClient({ ...client, full_name: e.target.value })} placeholder="Client name" className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" /></div>
            <div><Label className="text-zinc-300 text-sm">Email *</Label><Input type="email" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} placeholder="client@email.com" className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" /></div>
            <div><Label className="text-zinc-300 text-sm">Phone</Label><Input type="tel" value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} placeholder="+1 234 567" className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" /></div>
          </div>
        </div>

        {/* Installation Section */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="px-4 md:px-6 py-3 md:py-4 border-b border-zinc-800 flex items-center gap-3">
            <FileText className="w-5 h-5 text-red-500" />
            <h2 className="font-['Chivo'] text-base md:text-lg font-bold text-white">Installation Details</h2>
          </div>
          <div className="p-4 md:p-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              <div><Label className="text-zinc-300 text-sm">Date *</Label><Input type="date" value={installation.install_date} onChange={(e) => setInstallation({ ...installation, install_date: e.target.value })} className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" /></div>
              <div>
                <Label className="text-zinc-300 text-sm">PPF Type *</Label>
                <Select value={installation.ppf_type} onValueChange={(v) => setInstallation({ ...installation, ppf_type: v })}>
                  <SelectTrigger className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {PPF_TYPES.map((t) => (<SelectItem key={t.id} value={t.id} className="text-white hover:bg-zinc-800">{t.name} ({t.warranty}yr)</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300 text-sm">Roll Number *</Label>
                <Input value={installation.roll_number} onChange={(e) => setInstallation({ ...installation, roll_number: e.target.value })}
                  placeholder="ROLL-001" className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" />
              </div>
              <div>
                <Label className="text-zinc-300 text-sm">Service Price (€)</Label>
                <div className="relative mt-1.5">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm font-medium">€</span>
                  <Input type="number" step="0.01" value={installation.service_price}
                    onChange={(e) => setInstallation({ ...installation, service_price: e.target.value })}
                    placeholder="0.00" className="pl-8 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" />
                </div>
              </div>
            </div>

            {installation.ppf_type === 'COLOR_PPF' && (
              <div><Label className="text-zinc-300 text-sm">PPF Color *</Label><Input value={installation.ppf_color} onChange={(e) => setInstallation({ ...installation, ppf_color: e.target.value })} placeholder="Midnight Blue" className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" /></div>
            )}

            {warrantyYears > 0 && (
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                <p className="text-xs md:text-sm text-emerald-400 flex items-center gap-2"><Award className="w-4 h-4" /><strong>{warrantyYears} Year Warranty</strong> auto-applied</p>
              </div>
            )}

            {/* Flattened Coverage Areas */}
            <div>
              <Label className="text-zinc-300 text-sm block mb-3">Coverage Areas *</Label>
              <div className="space-y-4">
                {COVERAGE_CATEGORIES.map((category) => (
                  <div key={category} className="bg-zinc-950/30 rounded-lg p-3">
                    <p className="text-xs font-medium text-zinc-400 mb-2 uppercase tracking-wide">{category}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {COVERAGE_OPTIONS.filter(opt => opt.category === category).map((opt) => (
                        <label key={opt.id} 
                          className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors text-sm ${
                            coverage[opt.id] ? 'bg-red-600/10 border-red-500/30 text-white' : 'bg-zinc-950/50 border-zinc-700 hover:border-zinc-600 text-zinc-300'
                          }`}>
                          <Checkbox checked={!!coverage[opt.id]} onCheckedChange={(checked) => setCoverage(prev => ({ ...prev, [opt.id]: !!checked }))} className="h-4 w-4 shrink-0" />
                          <span className="text-xs sm:text-sm leading-tight">{opt.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div><Label className="text-zinc-300 text-sm">Notes</Label><Textarea value={installation.notes} onChange={(e) => setInstallation({ ...installation, notes: e.target.value })} placeholder="Additional notes..." className="mt-1.5 bg-zinc-950 border-zinc-700 text-white min-h-[60px] text-sm" /></div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <Label className="text-zinc-300 text-sm block mb-2">Warranty Card</Label>
                <input type="file" ref={warrantyCardRef} onChange={handleWarrantyCardChange} accept=".pdf,.jpg,.jpeg,.png" className="hidden" />
                {warrantyCard ? (
                  <div className="flex items-center gap-2 p-2.5 bg-zinc-950 border border-zinc-700 rounded-lg">
                    <File className="w-4 h-4 text-emerald-400" />
                    <span className="text-xs text-white flex-1 truncate">{warrantyCard.name}</span>
                    <button type="button" onClick={() => setWarrantyCard(null)} className="text-zinc-400 hover:text-red-400"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <Button type="button" variant="outline" onClick={() => warrantyCardRef.current?.click()} className="w-full border-dashed border-zinc-700 text-zinc-400 h-10 text-sm"><Upload className="w-4 h-4 mr-2" /> Upload</Button>
                )}
              </div>
              <div>
                <Label className="text-zinc-300 text-sm block mb-2">Photos (max 4)</Label>
                <input type="file" ref={photosRef} onChange={handlePhotosChange} accept=".jpg,.jpeg,.png" multiple className="hidden" />
                <Button type="button" variant="outline" onClick={() => photosRef.current?.click()} className="w-full border-dashed border-zinc-700 text-zinc-400 mb-2 h-10 text-sm"><Image className="w-4 h-4 mr-2" /> Add ({photos.length}/4)</Button>
                {photos.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {photos.map((p, i) => (
                      <div key={i} className="relative group">
                        <img src={p.data} alt="" className="w-10 h-10 object-cover rounded" />
                        <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100"><X className="w-3 h-3 text-white" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <Button type="button" variant="ghost" onClick={() => navigate('/partner')} className="flex-1 text-zinc-400 h-10">Cancel</Button>
          <Button type="submit" disabled={saving || vehicle.vin.length !== 17} className="flex-1 bg-red-600 hover:bg-red-700 text-white h-10">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</> : 'Save Installation'}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Account Settings
const AccountSettings = () => {
  const { user, getAuthHeaders, refreshUser } = useAuth();
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    company_name: user?.company_name || '',
    company_code: user?.company_code || '',
    vat_code: user?.vat_code || ''
  });
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [avatar, setAvatar] = useState(user?.avatar_base64 || null);
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const avatarRef = useRef(null);

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 2 * 1024 * 1024) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => setAvatar(reader.result);
    } else if (file) toast.error('Image too large. Max 2MB');
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/profile`, { ...profile, avatar_base64: avatar }, { headers: getAuthHeaders() });
      toast.success('Profile updated!');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwords.new !== passwords.confirm) { toast.error('Passwords do not match'); return; }
    if (passwords.new.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    
    setChangingPw(true);
    try {
      await axios.post(`${API_URL}/api/profile/change-password`, {
        current_password: passwords.current,
        new_password: passwords.new
      }, { headers: getAuthHeaders() });
      toast.success('Password changed!');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPw(false);
    }
  };

  return (
    <div data-testid="partner-settings" className="w-full max-w-6xl mx-auto">
      <h1 className="font-['Chivo'] text-xl md:text-2xl font-bold text-white mb-6 md:mb-8">Account Settings</h1>

      {/* Profile Section */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden mb-6 md:mb-8">
        <div className="px-4 md:px-6 py-3 md:py-4 border-b border-zinc-800">
          <h2 className="font-['Chivo'] text-base md:text-lg font-bold text-white">Profile Information</h2>
        </div>
        <div className="p-4 md:p-6 space-y-5">
          {/* Avatar */}
          <div className="flex items-center gap-4">
            <input type="file" ref={avatarRef} onChange={handleAvatarChange} accept=".jpg,.jpeg,.png" className="hidden" />
            {avatar ? (
              <img src={avatar} alt="" className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover" />
            ) : (
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-red-600/20 flex items-center justify-center">
                <User className="w-6 h-6 md:w-8 md:h-8 text-red-500" />
              </div>
            )}
            <div>
              <Button variant="outline" size="sm" onClick={() => avatarRef.current?.click()} className="border-zinc-700 text-zinc-300 h-8 text-sm">
                Change Avatar
              </Button>
              <p className="text-xs text-zinc-500 mt-1">Max 2MB, JPG/PNG</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div><Label className="text-zinc-300 text-sm">Representative Name</Label><Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" /></div>
            <div><Label className="text-zinc-300 text-sm">Email</Label><Input type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" /></div>
            <div><Label className="text-zinc-300 text-sm">Phone</Label><Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" /></div>
            <div><Label className="text-zinc-300 text-sm">Address</Label><Input value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" /></div>
          </div>

          <div className="border-t border-zinc-800 pt-5">
            <h3 className="text-sm font-medium text-zinc-400 mb-4">Company Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div><Label className="text-zinc-300 text-sm">Company Name</Label><Input value={profile.company_name} onChange={(e) => setProfile({ ...profile, company_name: e.target.value })} className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" /></div>
              <div><Label className="text-zinc-300 text-sm">Company Code</Label><Input value={profile.company_code} onChange={(e) => setProfile({ ...profile, company_code: e.target.value })} className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" /></div>
              <div><Label className="text-zinc-300 text-sm">VAT Code</Label><Input value={profile.vat_code} onChange={(e) => setProfile({ ...profile, vat_code: e.target.value })} className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" /></div>
            </div>
          </div>

          <Button onClick={handleSaveProfile} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white h-10 text-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save Changes
          </Button>
        </div>
      </div>

      {/* Password Section */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-4 md:px-6 py-3 md:py-4 border-b border-zinc-800">
          <h2 className="font-['Chivo'] text-base md:text-lg font-bold text-white">Change Password</h2>
        </div>
        <div className="p-4 md:p-6 space-y-4">
          <div>
            <Label className="text-zinc-300 text-sm">Current Password</Label>
            <div className="relative mt-1.5">
              <Input type={showPasswords.current ? "text" : "password"} value={passwords.current} onChange={(e) => setPasswords({ ...passwords, current: e.target.value })} className="pr-10 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" />
              <button type="button" onClick={() => setShowPasswords(p => ({ ...p, current: !p.current }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300" tabIndex={-1}>
                {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-zinc-300 text-sm">New Password</Label>
            <div className="relative mt-1.5">
              <Input type={showPasswords.new ? "text" : "password"} value={passwords.new} onChange={(e) => setPasswords({ ...passwords, new: e.target.value })} className="pr-10 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" />
              <button type="button" onClick={() => setShowPasswords(p => ({ ...p, new: !p.new }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300" tabIndex={-1}>
                {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-zinc-300 text-sm">Confirm New Password</Label>
            <div className="relative mt-1.5">
              <Input type={showPasswords.confirm ? "text" : "password"} value={passwords.confirm} onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} className="pr-10 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" />
              <button type="button" onClick={() => setShowPasswords(p => ({ ...p, confirm: !p.confirm }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300" tabIndex={-1}>
                {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <Button onClick={handleChangePassword} disabled={changingPw} className="bg-red-600 hover:bg-red-700 text-white h-10 text-sm">
            {changingPw ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Change Password
          </Button>
        </div>
      </div>
    </div>
  );
};

// Partner PPF Sales Component
const PartnerSales = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ ppf_type: '', paid: '' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [saving, setSaving] = useState(false);
  const { getAuthHeaders, user } = useAuth();
  
  // Updated empty form - NO client_name, NO vin, uses sale_date & quantity_type
  const emptyForm = {
    ppf_type: '', color: '', roll_number: '', 
    quantity_type: 'meters', quantity: '', 
    service_price: '', paid: false, 
    sale_date: new Date().toISOString().split('T')[0], notes: ''
  };
  const [form, setForm] = useState(emptyForm);

  const fetchSales = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/sales`, { headers: getAuthHeaders() });
      setSales(response.data);
    } catch (error) { toast.error('Failed to load sales data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSales(); }, []);

  const filteredSales = sales.filter(sale => {
    if (filters.ppf_type && sale.ppf_type !== filters.ppf_type) return false;
    if (filters.paid === 'paid' && !sale.paid) return false;
    if (filters.paid === 'unpaid' && sale.paid) return false;
    return true;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Updated validation - no client_name or vin required
    if (!form.ppf_type || !form.roll_number || !form.quantity || !form.service_price || !form.sale_date) {
      toast.error('Please fill all required fields'); return;
    }
    if (form.ppf_type === 'COLOR_PPF' && !form.color) {
      toast.error('Color is required for COLOR PPF'); return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ppf_type: form.ppf_type,
        color: form.ppf_type === 'COLOR_PPF' ? form.color : null,
        roll_number: form.roll_number,
        quantity_type: form.quantity_type,
        quantity: parseFloat(form.quantity),
        service_price: parseFloat(form.service_price),
        paid: form.paid,
        sale_date: form.sale_date,
        notes: form.notes || null
      };
      
      if (editingSale) {
        await axios.put(`${API_URL}/api/sales/${editingSale.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Sale updated successfully');
      } else {
        await axios.post(`${API_URL}/api/sales`, payload, { headers: getAuthHeaders() });
        toast.success('Sale created successfully');
      }
      
      setShowAddModal(false);
      setShowEditModal(false);
      setEditingSale(null);
      setForm(emptyForm);
      fetchSales();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save sale');
    } finally { setSaving(false); }
  };

  const handleEdit = (sale) => {
    setEditingSale(sale);
    // Updated to use new fields
    setForm({
      ppf_type: sale.ppf_type,
      color: sale.color || '',
      roll_number: sale.roll_number,
      quantity_type: sale.quantity_type || 'meters',
      quantity: sale.quantity.toString(),
      service_price: sale.service_price.toString(),
      paid: sale.paid,
      sale_date: (sale.sale_date || sale.install_date)?.split('T')[0] || '',
      notes: sale.notes || ''
    });
    setShowEditModal(true);
  };

  const handleDelete = async (saleId) => {
    if (!window.confirm('Are you sure you want to delete this sale?')) return;
    try {
      await axios.delete(`${API_URL}/api/sales/${saleId}`, { headers: getAuthHeaders() });
      toast.success('Sale deleted');
      fetchSales();
    } catch (error) { toast.error('Failed to delete sale'); }
  };

  const totalRevenue = filteredSales.reduce((sum, s) => sum + (s.service_price || 0), 0);
  const paidCount = filteredSales.filter(s => s.paid).length;
  const unpaidCount = filteredSales.filter(s => !s.paid).length;
  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>;

  return (
    <div data-testid="partner-ppf-sales" className="w-full space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="font-['Chivo'] text-xl md:text-2xl font-bold text-white">My PPF Sales</h1>
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => { setForm(emptyForm); setEditingSale(null); setShowAddModal(true); }} className="bg-red-600 hover:bg-red-700 text-white gap-2 h-9 text-sm">
            <Plus className="w-4 h-4" /> New Sale
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <p className="text-zinc-400 text-xs mb-1">Total Sales</p>
          <p className="font-['Chivo'] text-2xl font-bold text-white">{sales.length}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <p className="text-zinc-400 text-xs mb-1">Total Revenue</p>
          <p className="font-['Chivo'] text-2xl font-bold text-amber-400">€{totalRevenue.toLocaleString()}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <p className="text-zinc-400 text-xs mb-1">Paid</p>
          <p className="font-['Chivo'] text-2xl font-bold text-emerald-400">{paidCount}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <p className="text-zinc-400 text-xs mb-1">Unpaid</p>
          <p className="font-['Chivo'] text-2xl font-bold text-red-400">{unpaidCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-zinc-400" />
          <span className="text-sm text-zinc-400">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select value={filters.ppf_type || 'all'} onValueChange={(v) => setFilters({ ...filters, ppf_type: v === 'all' ? '' : v })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white h-10 text-sm"><SelectValue placeholder="All PPF Types" /></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="all" className="text-white">All PPF Types</SelectItem>
              {PPF_TYPES.map(t => <SelectItem key={t.id} value={t.id} className="text-white">{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.paid || 'all'} onValueChange={(v) => setFilters({ ...filters, paid: v === 'all' ? '' : v })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white h-10 text-sm"><SelectValue placeholder="All Status" /></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="all" className="text-white">All Status</SelectItem>
              <SelectItem value="paid" className="text-emerald-400">Paid</SelectItem>
              <SelectItem value="unpaid" className="text-red-400">Unpaid</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Sales Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase">PPF</th>
                <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase hidden lg:table-cell">Color</th>
                <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase hidden md:table-cell">Roll</th>
                <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase">Qty</th>
                <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase">Price</th>
                <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase">Status</th>
                <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase hidden lg:table-cell">Sales Date</th>
                <th className="text-right p-3 text-zinc-400 font-medium text-xs uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSales.map((sale) => (
                <tr key={sale.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                  <td className="p-3 text-white text-sm">{sale.ppf_type?.replace('_', ' ')}</td>
                  <td className="p-3 text-zinc-400 text-sm hidden lg:table-cell">{sale.color || '-'}</td>
                  <td className="p-3 font-mono text-zinc-400 text-xs hidden md:table-cell">{sale.roll_number}</td>
                  <td className="p-3 text-zinc-300 text-sm">{sale.quantity} {(sale.quantity_type === 'rolls') ? 'rolls' : 'm'}</td>
                  <td className="p-3 text-amber-400 font-medium text-sm">€{sale.service_price?.toLocaleString()}</td>
                  <td className="p-3">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${sale.paid ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                      {sale.paid ? 'Paid' : 'Unpaid'}
                    </span>
                  </td>
                  <td className="p-3 text-zinc-500 text-xs hidden lg:table-cell">{formatDate(sale.sale_date || sale.install_date)}</td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(sale)} className="text-zinc-400 hover:text-white h-8 w-8 p-0">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(sale.id)} className="text-zinc-400 hover:text-red-400 h-8 w-8 p-0">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredSales.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-zinc-500">No sales found. Click &quot;New Sale&quot; to add one.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Sale Modal */}
      <Dialog open={showAddModal || showEditModal} onOpenChange={(open) => { if (!open) { setShowAddModal(false); setShowEditModal(false); setEditingSale(null); } }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-xl text-white">{editingSale ? 'Edit Sale' : 'Add New Sale'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* PPF Type */}
            <div>
              <Label className="text-zinc-300 text-sm">PPF Type *</Label>
              <Select value={form.ppf_type} onValueChange={(v) => setForm({ ...form, ppf_type: v, color: v !== 'COLOR_PPF' ? '' : form.color })}>
                <SelectTrigger className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm"><SelectValue placeholder="Select PPF Type" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  {PPF_TYPES.map(t => <SelectItem key={t.id} value={t.id} className="text-white">{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Color - only for COLOR_PPF */}
            {form.ppf_type === 'COLOR_PPF' && (
              <div>
                <Label className="text-zinc-300 text-sm">Color *</Label>
                <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" placeholder="e.g., Midnight Blue" />
              </div>
            )}
            {/* Roll Number & Service Price */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300 text-sm">Roll Number *</Label>
                <Input value={form.roll_number} onChange={(e) => setForm({ ...form, roll_number: e.target.value })}
                  className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" placeholder="ROLL-001" />
              </div>
              <div>
                <Label className="text-zinc-300 text-sm">Service Price (€) *</Label>
                <div className="relative mt-1.5">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">€</span>
                  <Input type="number" step="0.01" value={form.service_price} onChange={(e) => setForm({ ...form, service_price: e.target.value })}
                    className="pl-7 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" placeholder="500.00" />
                </div>
              </div>
            </div>
            {/* Quantity Type & Quantity */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300 text-sm">Quantity Type *</Label>
                <Select value={form.quantity_type} onValueChange={(v) => setForm({ ...form, quantity_type: v })}>
                  <SelectTrigger className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="meters" className="text-white">Meters (linear)</SelectItem>
                    <SelectItem value="rolls" className="text-white">Rolls</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300 text-sm">Quantity ({form.quantity_type === 'rolls' ? 'rolls' : 'meters'}) *</Label>
                <Input type="number" step={form.quantity_type === 'rolls' ? '1' : '0.1'} value={form.quantity} 
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" 
                  placeholder={form.quantity_type === 'rolls' ? '2' : '5.5'} />
              </div>
            </div>
            {/* Sales Date */}
            <div>
              <Label className="text-zinc-300 text-sm">Sales Date *</Label>
              <Input type="date" value={form.sale_date} onChange={(e) => setForm({ ...form, sale_date: e.target.value })}
                className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" />
            </div>
            {/* Paid Checkbox */}
            <div className="flex items-center gap-3">
              <Checkbox checked={form.paid} onCheckedChange={(checked) => setForm({ ...form, paid: checked })} id="partner-paid" />
              <Label htmlFor="partner-paid" className="text-zinc-300 text-sm cursor-pointer">Paid</Label>
            </div>
            {/* Notes */}
            <div>
              <Label className="text-zinc-300 text-sm">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1.5 bg-zinc-950 border-zinc-700 text-white text-sm min-h-[60px]" placeholder="Additional notes..." />
            </div>
            <DialogFooter className="mt-4">
              <Button type="button" variant="ghost" onClick={() => { setShowAddModal(false); setShowEditModal(false); }} className="text-zinc-400">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-red-600 hover:bg-red-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} {editingSale ? 'Update' : 'Create'} Sale
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function PartnerDashboard() {
  const { user } = useAuth();
  const isDemo = user?.role === 'demo_partner';
  
  return (
    <div className="min-h-screen bg-[#09090b] lg:flex flex-col" data-testid="partner-dashboard">
      {/* Demo Account Watermark */}
      {isDemo && (
        <div className="bg-red-600/90 text-white py-2 px-4 text-center lg:ml-64">
          <span className="font-bold">Demo Partner</span> — Data shown here is for demonstration only.
        </div>
      )}
      <div className="flex-1 lg:flex">
        <Sidebar />
        <main className={`flex-1 p-4 lg:p-8 overflow-auto pb-12 pt-16 lg:pt-8`}>
          <Routes>
            <Route index element={<MyInstallations />} />
            <Route path="add-installation" element={<AddInstallation />} />
            <Route path="add-car" element={<AddInstallation />} />
            <Route path="settings" element={<AccountSettings />} />
          </Routes>
        </main>
      </div>
      {/* Footer */}
      <footer className="bg-[#09090b] border-t border-white/5 py-3 text-center">
        <p className="text-zinc-500 text-xs">© 2026 Unique Protection. All rights reserved.</p>
      </footer>
    </div>
  );
}
